import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { HelperService } from 'src/app/public/helper.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { StoresActionType } from 'src/app/stores/+state/stores.actions';
import { Actions, ofType } from '@ngrx/effects';

/**
 * What the tile holds. `video` is the kiosk inactivity video and the default;
 * `pdf` is the Marketing brochure (story #11626). The two differ in validation,
 * in the rejection toast, in whether a thumbnail can be rendered, and in whether
 * the caption survives once a file exists — everything else is shared.
 */
export type UploadFileKind = 'video' | 'pdf';

/**
 * The shape the tile is drawn in. `tile` is the 100 by 200 pixel portrait tile
 * this component has always drawn — the kiosk inactivity video and the
 * Marketing brochure — and is the default, so neither screen changes. `banner`
 * is the full-width band the store header video needs (story #11770), sized to
 * line up under the store image band on Store details.
 */
export type UploadTileVariant = 'tile' | 'banner';

const BYTES_PER_MB = 1024 * 1024;

@Component({
  selector: 'app-upload-video',
  templateUrl: './upload-video.component.html',
  styleUrl: './upload-video.component.scss',
})
export class UploadVideoComponent implements OnInit, OnChanges, OnDestroy {
  @Input() currentVideoUrl: string;
  @Input() label: string;
  @Input() infoMessage: string;
  @Input() acceptedFormats: string = '.mp4, .webm, .mov';
  /**
   * Check the picked file against `acceptedFormats` as well as handing the list
   * to the picker. `accept` is only a filter in the file dialog, so a merchant
   * who switches it to "All files" can pick an `.avi` or `.mkv`, and the generic
   * `video/*` check below lets it through to an upload the backend then refuses
   * with a bare 400.
   *
   * Off by default: the kiosk inactivity video and the brochure must keep
   * exactly the check they have today (story #11770 requires both screens to be
   * left unchanged by this work). The store header video opts in, because the
   * story names exactly three containers and the backend's
   * `STORE_HEADER_VIDEO` accepts exactly those three content types.
   */
  @Input() enforceAcceptedFormats: boolean = false;
  @Input() submitVideo: (file: File) => void;
  @Input() removeVideo: () => void;

  /** Drives validation and display. Defaults to the kiosk video behaviour. */
  @Input() fileKind: UploadFileKind = 'video';
  /** The box the tile is drawn in. Defaults to today's portrait tile. */
  @Input() variant: UploadTileVariant = 'tile';
  /** Caption shown while the tile is empty. */
  @Input() uploadText: string = 'admin.store.events.uploadReel';
  /**
   * Caption shown once a file exists. Left unset for the video, which shows its
   * thumbnail instead of a caption — the PDF has no thumbnail, so it needs one.
   */
  @Input() replaceText: string;
  /**
   * Client-side size guard, in MB. Unset means only the backend 413 applies.
   * The rejection reuses the fixed "…50 MB" message, so a caller sets this to
   * `PLATFORM_MAX_UPLOAD_SIZE_MB`.
   */
  @Input() maxSizeMb: number;
  /** Renders a download icon beside the tile once a file exists. */
  @Input() showDownload: boolean = false;
  /** File name suggested to the browser by the download icon. */
  @Input() downloadFileName: string = 'download';

  reelImage: Object;
  video: File;
  isUploading: boolean = false;
  private cachedImageUrl: { [key: string]: any } = {};
  private destroy$ = new Subject<void>();

  constructor(
    private translateService: TranslateService,
    private toastr: ToastrService,
    private helperService: HelperService,
    private sanitizer: DomSanitizer,
    private store: Store,
    private actions$: Actions,
  ) {}

  ngOnInit(): void {
    this.generateThumbnail(this.currentVideoUrl);
    this.setupUploadListeners();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupUploadListeners(): void {
    // Listen for upload success
    this.actions$
      .pipe(
        ofType(StoresActionType.UploadStoreVideoSuccess),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.isUploading = false;
      });

    // Listen for upload failure
    this.actions$
      .pipe(
        ofType(StoresActionType.UploadStoreVideoFailed),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.isUploading = false;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.generateThumbnail(this.currentVideoUrl);
  }

  generateThumbnail(videoUrl: string) {
    this.reelImage = null;
    // A PDF has no frame to grab, and the helper would reject the URL anyway.
    // The banner has no use for a grabbed frame either: it renders the stored
    // clip in a <video>, which also works where the canvas grab cannot (the
    // helper taints the canvas on a cross-origin file, which is every file in
    // the admin app, where the page runs on localhost).
    if (this.fileKind === 'pdf' || this.isBanner) {
      return;
    }
    if (videoUrl) {
      this.helperService.generateThumbnail(videoUrl).then((res) => {
        this.reelImage = this.getImage(res);
      });
    }
  }

  getImage(image: string) {
    if (!this.cachedImageUrl[image]) {
      this.cachedImageUrl[image] = this.sanitizer.bypassSecurityTrustStyle(
        `url('${image}')`,
      );
    }
    return this.cachedImageUrl[image];
  }

  /** The full-width band variant; see {@link UploadTileVariant}. */
  get isBanner(): boolean {
    return this.variant === 'banner';
  }

  /**
   * A thumbnail is only ever available for a video drawn as the portrait tile.
   * Everything else the tile shows is derived from this, so the PDF path and
   * the banner keep the cloud icon where that tile shows its frame.
   */
  get showThumbnail(): boolean {
    return this.fileKind === 'video' && !this.isBanner && !!this.reelImage;
  }

  /** The banner shows the stored clip itself rather than a grabbed frame. */
  get showBannerVideo(): boolean {
    return this.isBanner && !!this.currentVideoUrl;
  }

  /**
   * Whether a file is already stored, which is what shows the ✕. The portrait
   * video tile reads its generated thumbnail, because that is also what it
   * shows in place of a caption. Every other shape reads the URL — the banner
   * must, because it never generates a thumbnail, and reading one would leave a
   * merchant whose clip the admin browser cannot decode with no way to remove
   * it.
   */
  get hasFile(): boolean {
    return this.fileKind === 'video' && !this.isBanner
      ? !!this.reelImage
      : !!this.currentVideoUrl;
  }

  /** An empty tile shows the upload caption; a filled one shows `replaceText`. */
  get captionKey(): string {
    return this.hasFile && this.replaceText
      ? this.replaceText
      : this.uploadText;
  }

  /**
   * With no `replaceText` the caption disappears once a file exists, because
   * the video's thumbnail takes its place.
   */
  get showCaption(): boolean {
    return !this.hasFile || !!this.replaceText;
  }

  selectVideo(event) {
    event.target.value = null;
  }

  uploadVideo(fileEvent: Event) {
    const fileInput = fileEvent.target as HTMLInputElement;

    if (fileInput?.files?.length) {
      this.video = fileInput.files[0];
      if (!this.isAcceptedFile(this.video)) {
        this.showRejection(this.invalidFormatMessageKey());
        this.video = null;
        return;
      }

      if (this.exceedsMaxSize(this.video)) {
        this.showRejection('admin.store.settings.fileSizeError');
        this.video = null;
        return;
      }

      this.isUploading = true;
      this.submitVideo(this.video);
    }
  }

  /**
   * Some systems hand back an empty MIME type for a PDF, so the extension is
   * accepted as a fallback. The backend has the final say: it keeps the file
   * only if its first bytes are `%PDF-`, whatever type the browser sent.
   */
  private isAcceptedFile(file: File): boolean {
    if (this.fileKind === 'pdf') {
      return (
        file.type === 'application/pdf' ||
        !!file.name?.toLowerCase().endsWith('.pdf')
      );
    }
    if (this.enforceAcceptedFormats) {
      return this.matchesAcceptedFormats(file);
    }
    return file.type.startsWith('video/');
  }

  /**
   * The declared extensions, matched on the file name. The extension rather
   * than the MIME type, because that is the form `acceptedFormats` is written
   * in and the form the picker filters on, and because a container the
   * operating system does not recognise arrives with an empty `file.type` —
   * which would refuse a perfectly good `.mov` on a machine with no video
   * application registered. The backend keeps the final say: it checks the
   * declared content type against the same three types, so a renamed `.avi`
   * is still refused there.
   *
   * An empty or extension-less list accepts everything, so switching the flag
   * on without a list cannot silently refuse every file.
   */
  private matchesAcceptedFormats(file: File): boolean {
    const allowed = (this.acceptedFormats ?? '')
      .split(',')
      .map((format) => format.trim().toLowerCase())
      .filter((format) => format.startsWith('.') && format.length > 1);
    if (!allowed.length) {
      return true;
    }
    const name = file.name?.toLowerCase() ?? '';
    return allowed.some((extension) => name.endsWith(extension));
  }

  private invalidFormatMessageKey(): string {
    return this.fileKind === 'pdf'
      ? 'admin.store.events.invalidFileFormat'
      : 'admin.store.events.invalidVideoFormat';
  }

  private exceedsMaxSize(file: File): boolean {
    return !!this.maxSizeMb && file.size > this.maxSizeMb * BYTES_PER_MB;
  }

  /**
   * Toast for a file the tile refuses to send. The video keeps its own toast:
   * the message plus a full stop, under the English title "Upload failed!".
   * The PDF shows the message as written, under the translated title.
   */
  private showRejection(messageKey: string): void {
    const message = this.translateService.instant(messageKey);
    if (this.fileKind === 'pdf') {
      this.toastr.error(
        message,
        this.translateService.instant('admin.store.message.uploadFail'),
      );
      return;
    }
    this.toastr.error(`${message}.`, 'Upload failed!');
  }

  /**
   * Same approach as `downloadImage()` in `promotion-form.component.ts`: the
   * stored file is served with `Content-Disposition: attachment`, so a plain
   * link click is all a download needs.
   *
   * `downloadFileName` is a best-effort hint only. On the web admin the file
   * is served from the admin's own origin (`<admin>/api/v1/files/...`), where
   * the `download` attribute applies. In the apps the page runs on
   * `localhost`, so the URL is cross-origin and browsers ignore `download`;
   * the saved name then comes from the backend's `Content-Disposition`
   * (`store-brochure.pdf` for the brochure).
   *
   * The anchor is appended before clicking: a detached one works in current
   * Chrome, Safari and Firefox, but not in every older WebView.
   */
  downloadFile() {
    if (!this.isDownloadableUrl(this.currentVideoUrl)) {
      return;
    }
    const link = document.createElement('a');
    link.href = this.currentVideoUrl;
    link.download = this.downloadFileName;
    document.body.appendChild(link);
    try {
      link.click();
    } finally {
      document.body.removeChild(link);
    }
  }

  /**
   * The backend only lets an upload set the brochure URL, but this link runs
   * in the admin origin, so it does not rely on that: clicking a `javascript:`
   * link runs it even with `download` set, so only http(s) is followed.
   * Parsing, rather than a prefix check, also catches the case, whitespace and
   * control-character variants a browser still reads as `javascript:`.
   */
  private isDownloadableUrl(url: string): boolean {
    if (!url) {
      return false;
    }
    try {
      const { protocol } = new URL(url, window.location.href);
      return protocol === 'http:' || protocol === 'https:';
    } catch {
      // Not a URL at all, e.g. `https://` with no host.
      return false;
    }
  }

  processRemoveVideo() {
    this.removeVideo();
  }
}
